import { defineStore, acceptHMRUpdate } from 'pinia';
import { computed, ref, toRefs } from 'vue';
import { IMPERSONATE_MUTATION } from '../graphql/mutations/auth';
import type { Admin, TokenLoginResponse } from '../graphql/types/auth';
import { CombinedError } from '@urql/core';
import { useUrqlStore } from './useUrqlStore';

import { ME_QUERY } from '../graphql/queries/auth';
import { MeResponse } from '../graphql/types/auth';

const storeSetupIntake = () => {
  const user = ref<Admin | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const expiration = ref<number | null>(null);
  const isLoading = ref(false);
  const isAdvisor = ref(false);

  const setIsAdvisor = (value: boolean) => isAdvisor.value = value;

  const updateIntakeTokens = (
    _accessToken: string | null,
    _refreshToken: string | null,
    _expiration: number | null,
  ) => {
    accessToken.value = _accessToken;
    refreshToken.value = _refreshToken;
    expiration.value = _expiration;
  };

  const login = (
    _user: Admin,
    _accessToken: string,
    _refreshToken: string,
    _expiration: number,
  ) => {
    user.value = _user;
    updateIntakeTokens(_accessToken, _refreshToken, _expiration);
  };

  const logout = () => {
    user.value = null;
    updateIntakeTokens(null, null, null);
  };

  const resetState = () => {
    logout();
    isAdvisor.value = false;
  }

  const authenticated = computed(() => !isLoading.value && user.value !== null);

  const isAdvisorWithMandate = computed(() => user.value?.roles?.filter(role => role.type === 'ADVISOR')?.[0]?.role_data?.has_mandate);

  const tokenLogin = (token: string, redirect: string): Promise<string | void> => {
    isLoading.value = true;
    return new Promise((resolve, _reject) => {
      const { gqlClient } = toRefs(useUrqlStore());

      gqlClient.value
        .mutation<TokenLoginResponse>(IMPERSONATE_MUTATION, {
          input: {
            token,
          },
        })
        .toPromise()
        .then(result => {
          if (result.data) {
            login(
              result.data.tokenLogin.user,
              result.data.tokenLogin.access_token,
              result.data.tokenLogin.refresh_token,
              Number(result.data.tokenLogin.expires_in),
            );
            navigateTo({ path: redirect });
            resolve();
          }

          if (result.error) {
            resolve(result.error.message);
          }
        })
        .catch((error: CombinedError) => {
          showError(error);
        })
        .finally(() => {
          isLoading.value = false;
        });
    });
  };

  const refreshUser = async () => {
    const { gqlClient } = toRefs(useUrqlStore());

    return accessToken.value ? gqlClient.value
      .query<MeResponse>(ME_QUERY, undefined)
      .toPromise()
      .then((result) => {
        if (result.data?.me) {
          user.value = result.data.me;
        } else {
          updateIntakeTokens(null, null, null);
        }
      })
      .catch(() => {
        updateIntakeTokens(null, null, null);
      }) : null;
  };

  return {
    // State
    user,
    accessToken,
    refreshToken,
    expiration,
    isLoading,
    isAdvisor,

    // Getters
    authenticated,
    isAdvisorWithMandate,

    // Actions
    login,
    logout,
    updateIntakeTokens,
    tokenLogin,
    refreshUser,
    setIsAdvisor,
    resetState,
  };
};

export const useIntakeAuthStore = defineStore('intakeAuth', storeSetupIntake, {
  persistedState: {
    merge: (state, savedState) => {
      state.accessToken = savedState.accessToken ?? null;
      state.refreshToken = savedState.refreshToken ?? null;
      state.expiration = savedState.expiration ?? null;
      state.isAdvisor = savedState.isAdvisor ?? false;

      return state;
    },
    persist: true,
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useIntakeAuthStore, import.meta.hot));
}
